// src/pages/secret/SecretAuditPage.tsx
// Immutable audit trail — every read/write/encrypt/decrypt/cert/lease/seal operation.
// See docs/ux/secret-ia.md section 4. Admin-gated (AdminRoute, in App.tsx).

import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useI18n, useSecretAudit } from '@react/hooks';
import { Card, Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty, ErrorAlert, Input } from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import type { SecretAuditEntry } from '@core/types/secret';
import { formatDateTime } from '@/features/secret/logic';

function operationLabel(op?: string): string {
  return (op ?? '').replace('SECRET_AUDIT_OPERATION_', '').replace(/_/g, ' ').toLowerCase();
}

export const SecretAuditPage = observer(() => {
  const { t } = useI18n();
  const store = useSecretAudit();
  const [entries, setEntries] = useState<SecretAuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [pathFilter, setPathFilter] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    store.ListAuditEntries()
      .then((resp) => setEntries(resp.entries ?? []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [store]);

  useEffect(() => { load(); }, [load]);

  const filtered = pathFilter ? entries.filter((e) => e.path.includes(pathFilter)) : entries;

  return (
    <div className="space-y-6 p-6">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex items-center justify-between">
        <h1 className="sr-only">{t('secret.audit.title')}</h1>
        <Link to="/secrets" className="ml-auto text-sm text-primary hover:underline">{t('secret.console.backToConsole')}</Link>
      </div>

      {store.error != null && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}

      <div className="max-w-xs">
        <Input value={pathFilter} onChange={(e) => setPathFilter(e.target.value)} placeholder={t('secret.audit.filterPathPlaceholder')} />
      </div>

      <Card>
        <Table hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>{t('secret.audit.column.time')}</TableHead>
              <TableHead>{t('secret.audit.column.operation')}</TableHead>
              <TableHead>{t('secret.audit.column.path')}</TableHead>
              <TableHead>{t('secret.audit.column.actor')}</TableHead>
              <TableHead>{t('secret.audit.column.result')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">{t('common.loading')}</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableEmpty colSpan={5}>
                <span className="table-empty-message">{t('secret.audit.empty')}</span>
                <p className="mt-1 text-sm text-muted-foreground">{t('secret.audit.subtitle')}</p>
              </TableEmpty>
            ) : (
              filtered.map((entry, i) => (
                <TableRow key={entry.entity?.id ?? `${entry.timestamp}-${i}`}>
                  <TableCell className="text-sm text-muted-foreground">{formatDateTime(entry.timestamp)}</TableCell>
                  <TableCell className="text-sm text-foreground">{operationLabel(entry.operation)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{entry.path}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{entry.actor?.display_name || entry.actor?.entity?.id || '—'}</TableCell>
                  <TableCell>
                    <StatusBadge variant={entry.success ? 'success' : 'error'} label={entry.success ? t('secret.audit.success') : t('secret.audit.failure')} size="sm" />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default SecretAuditPage;
